
import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { X, Upload } from "lucide-react";

interface CategoryEditPopoverProps {
  category: { 
    name: string; 
    iconSrc?: string;
  };
  trigger: React.ReactNode;
  onUpdate: (categoryName: string, newName: string, newIconSrc?: string) => void;
  onDelete: (categoryName: string) => void;
}

export const CategoryEditPopover = ({
  category,
  trigger,
  onUpdate,
  onDelete,
}: CategoryEditPopoverProps) => {
  const [tempName, setTempName] = useState(category.name);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(category.iconSrc || null);
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setTempName(category.name);
      setPreviewUrl(category.iconSrc || null);
      setSelectedFile(null);
    }
  }, [open, category]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select an image under 2MB",
          variant: "destructive",
        });
        return;
      }
      
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid file type",
          description: "Please select an image file",
          variant: "destructive",
        });
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    try {
      if (!tempName.trim()) {
        toast({
          title: "Error",
          description: "Category name cannot be empty",
          variant: "destructive",
        });
        return;
      }

      let iconSrc = category.iconSrc;
      
      // If there's a new file selected, convert it to base64
      if (selectedFile) {
        const reader = new FileReader();
        iconSrc = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(selectedFile);
        });
      } else if (previewUrl !== category.iconSrc) {
        // If user cleared the icon
        iconSrc = previewUrl;
      }

      onUpdate(category.name, tempName.trim(), iconSrc);
      setOpen(false);
      toast({
        title: "Success",
        description: "Category has been updated successfully.",
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = () => {
    try {
      onDelete(category.name);
      setOpen(false);
      toast({
        title: "Success",
        description: "Category has been deleted successfully.",
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Error",
        description: "Failed to delete category. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[313px]">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-lg font-semibold">Edit Category</Label>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-gray-100"
              onClick={() => setOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-2">
            <Label>Category Name</Label>
            <Input
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              className="border-primary/30 focus:border-primary rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label>Category Icon</Label>
            <div className="flex items-center gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={triggerFileInput}
                className="flex-1"
              >
                <Upload className="h-4 w-4 mr-2" />
                {category.iconSrc ? "Change Icon" : "Add Icon"}
              </Button>
              {previewUrl && (
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setPreviewUrl(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {previewUrl && (
              <div className="mt-2 flex justify-center">
                <div className="border rounded-md p-2 bg-gray-50 w-16 h-16 flex items-center justify-center">
                  <img 
                    src={previewUrl} 
                    alt="Category icon preview" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              className="flex-1 bg-primary hover:bg-primary/90 text-white"
              onClick={handleSave}
            >
              Save Changes
            </Button>
            <Button
              variant="outline"
              className="flex-1 border-[#ea384c] text-[#ea384c] hover:bg-[#ea384c] hover:text-white transition-colors"
              onClick={handleDelete}
            >
              Delete Category
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
